/*
 * Copyright (c) Funtip GbR 2018.
 * Simon Oldeboershuis, Perry Prust
 */

import { all, put, select, takeLatest, throttle } from 'redux-saga/effects';
import { Action } from 'typescript-fsa/dist/typescript-fsa';
import { fetchWrapperFuncNoLoading } from '../../utils';
import { getSystemConfig, getUserGroupApi2 } from '../System/systemConfig';
import { groupHighscoresLoadDefaultRound } from '../UserGroupHighscores/actions';
import {
    countUserGroups,
    COUNT_USER_GROUPS,
    fetchUserGroups,
    FETCH_USER_GROUPS,
    PAGE_DOWN_USER_GROUPS,
    PAGE_FIRST_USER_GROUPS,
    PAGE_LAST_USER_GROUPS,
    PAGE_UP_USER_GROUPS,
    SEARCH_USER_GROUPS,
    setUserGroupCount,
    setUserGroups,
    setUserGroupSearchString,
    setUserGroupsNextEnabled,
    setUserGroupsPriorEnabled,
    setUserGroupStartPos,
    userGroupsIsLoading,
} from './actions';
import { UserGroupListStore } from './reducer';
import * as selectors from './selectors';

const CLIENT_ID: number = getSystemConfig().clientId;


const getLastPageStart = (count: number, slotSize: number) => {
    return Math.floor(count / slotSize) * slotSize;
};

function* searchUserGroupsGenerator(action: Action<string>) {
    yield put(setUserGroupSearchString(action.payload));
    yield put(setUserGroupStartPos(0));
    yield put(fetchUserGroups());
}

function* fetchUserGroupsGenerator() {
    const userGroupApi = getUserGroupApi2();
    yield put(userGroupsIsLoading(true));
    try {
        let state: UserGroupListStore = yield select(selectors.userGroupListSelector);

        yield put(countUserGroups());

        let ugList = yield* fetchWrapperFuncNoLoading(
            () => userGroupApi.searchUserGroups(CLIENT_ID, state.startPos, state.slotSize, state.searchString),
            [(r) => setUserGroups(r)]
        );
        if (ugList) {
            for (let ug of ugList) {
                yield put(groupHighscoresLoadDefaultRound(String(ug.id)));
            }
        }
    } finally {
        yield put(userGroupsIsLoading(false));
    }
}

function* countUserGroupsGenerator() {
    const userGroupApi = getUserGroupApi2();
    let state: UserGroupListStore = yield select(selectors.userGroupListSelector);

    let count = yield* fetchWrapperFuncNoLoading(
        () => userGroupApi.countUserGroups(CLIENT_ID, state.searchString),
        [(r) => setUserGroupCount(r)]
    );
    if (count) {
        let priorEnabled: boolean = state.startPos > 0;
        let nextEnabled: boolean = state.startPos < getLastPageStart(count, state.slotSize);
        yield put(setUserGroupsNextEnabled(nextEnabled));
        yield put(setUserGroupsPriorEnabled(priorEnabled));
    }
}

function* pageUpUserGroupsGenerator() {
    let state: UserGroupListStore = yield select(selectors.userGroupListSelector);
    let newStart: number = state.startPos - state.slotSize;

    if (newStart >= 0) {
        yield put(setUserGroupStartPos(newStart));
        yield put(fetchUserGroups());
    }
}

function* pageDownUserGroupsGenerator() {
    let state: UserGroupListStore = yield select(selectors.userGroupListSelector);
    let newStart: number = state.startPos + state.slotSize;

    if (newStart <= state.count) {
        yield put(setUserGroupStartPos(newStart));
        yield put(fetchUserGroups());
    }
}

function* pageLastUserGroupsGenerator() {
    let state: UserGroupListStore = yield select(selectors.userGroupListSelector);
    let newStart: number = getLastPageStart(state.count, state.slotSize);

    yield put(setUserGroupStartPos(newStart));
    yield put(fetchUserGroups());
}

function* pageFirstUserGroupsGenerator() {
    let newStart: number = 0;

    yield put(setUserGroupStartPos(newStart));
    yield put(fetchUserGroups());
}

function* watchForUserGroupListActions() {
    yield takeLatest(PAGE_FIRST_USER_GROUPS, pageFirstUserGroupsGenerator);
    yield takeLatest(PAGE_LAST_USER_GROUPS, pageLastUserGroupsGenerator);
    yield takeLatest(PAGE_UP_USER_GROUPS, pageUpUserGroupsGenerator);
    yield takeLatest(PAGE_DOWN_USER_GROUPS, pageDownUserGroupsGenerator);
    yield takeLatest(COUNT_USER_GROUPS, countUserGroupsGenerator);
    yield takeLatest(FETCH_USER_GROUPS, fetchUserGroupsGenerator);
    yield throttle(500, SEARCH_USER_GROUPS, searchUserGroupsGenerator);
}

export function* userGroupListSagas() {
    yield all([watchForUserGroupListActions()]);
}
